import { useId } from 'react';

type MoneyGivenCashWatermarkProps = {
  className?: string;
  /** Lighter pastel palette for helper home and other low-contrast cards. */
  tone?: 'default' | 'subtle';
  /** Compact crop for inline title decoration; aligns visual bottom via preserveAspectRatio. */
  inline?: boolean;
  /** When true, render at full opacity (default watermark uses ~0.24). */
  opaque?: boolean;
};

/** Inline crop — bills + floating coins; width includes coinC on the right (x≈664). */
const TITLE_INLINE_VIEWBOX = '50 15 670 400';

type CoinColors = {
  shadow: string;
  mid: string;
  face: string;
  text: string;
};

type MoneyScenePalette = {
  billBack: { outer: string; mid: string; inner: string; corner: string; circle: string };
  billFront: {
    outer: string;
    mid: string;
    inner: string;
    corner: string;
    circle: string;
    highlight: string;
    fold: string;
  };
  coinA: CoinColors;
  coinB: CoinColors;
  coinC: CoinColors;
  stack: {
    layerADark: string;
    layerAMid: string;
    layerAEllipse: string;
    layerBDark: string;
    layerBMid: string;
    layerBEllipse: string;
    topCoin: CoinColors;
  };
  roll: {
    body: string;
    stripeA: string;
    stripeB: string;
    shine: string;
    topCoinMid: string;
    topCoinFace: string;
    topCoinText: string;
  };
};

/** Decorative cash & coins watermark for Money Given / Money Received dashboard cards. */
export const MoneyGivenCashWatermark = ({
  className = '',
  tone = 'default',
  inline = false,
  opaque = false,
}: MoneyGivenCashWatermarkProps) => {
  const uid = useId().replace(/:/g, '');
  const lightSceneId = `moneyGivenScene-light-${uid}`;
  const darkSceneId = `moneyGivenScene-dark-${uid}`;
  const shadowId = `moneyGivenShadow-${uid}`;

  const renderDollarCoin = (
    colors: CoinColors,
    scale: number,
    fontSize: number,
    textY: number,
    radius = 60,
    innerRadius = 45,
  ) => (
    <g transform={`scale(${scale})`}>
      <circle cx="60" cy="60" r={radius} fill={colors.shadow} />
      <circle cx="60" cy="55" r={radius} fill={colors.mid} />
      <circle cx="60" cy="55" r={innerRadius} fill={colors.face} />
      <text
        x="60"
        y={textY}
        fontFamily="Arial, sans-serif"
        fontSize={fontSize}
        textAnchor="middle"
        fill={colors.text}
        fontWeight="bold"
      >
        $
      </text>
    </g>
  );

  const renderScene = (sceneId: string, palette: MoneyScenePalette, includeBottomCoins: boolean) => (
    <g id={sceneId}>
      <g transform="translate(180, 150) rotate(5)">
        <rect x="0" y="0" width="460" height="250" rx="16" fill={palette.billBack.outer} />
        <rect x="15" y="15" width="430" height="220" rx="8" fill={palette.billBack.mid} />
        <rect x="25" y="25" width="410" height="200" rx="4" fill={palette.billBack.inner} />
        <path d="M 25 55 A 30 30 0 0 1 55 25" fill="none" stroke={palette.billBack.corner} strokeWidth="8" />
        <path d="M 405 25 A 30 30 0 0 1 435 55" fill="none" stroke={palette.billBack.corner} strokeWidth="8" />
        <path d="M 435 195 A 30 30 0 0 1 405 225" fill="none" stroke={palette.billBack.corner} strokeWidth="8" />
        <path d="M 55 225 A 30 30 0 0 1 25 195" fill="none" stroke={palette.billBack.corner} strokeWidth="8" />
        <circle cx="230" cy="125" r="55" fill={palette.billBack.circle} />
        <circle cx="85" cy="125" r="22" fill={palette.billBack.circle} />
        <circle cx="375" cy="125" r="22" fill={palette.billBack.circle} />
      </g>

      <g transform="translate(100, 110) rotate(-16)">
        <rect x="0" y="0" width="460" height="250" rx="16" fill={palette.billFront.outer} />
        <rect x="15" y="15" width="430" height="220" rx="8" fill={palette.billFront.mid} />
        <path d="M 320 235 L 445 235 L 445 110 Z" fill={palette.billFront.fold} opacity="0.3" />
        <rect x="25" y="25" width="410" height="200" rx="4" fill={palette.billFront.inner} />
        <path d="M 25 55 A 30 30 0 0 1 55 25" fill="none" stroke={palette.billFront.corner} strokeWidth="8" />
        <path d="M 405 25 A 30 30 0 0 1 435 55" fill="none" stroke={palette.billFront.corner} strokeWidth="8" />
        <path d="M 435 195 A 30 30 0 0 1 405 225" fill="none" stroke={palette.billFront.corner} strokeWidth="8" />
        <path d="M 55 225 A 30 30 0 0 1 25 195" fill="none" stroke={palette.billFront.corner} strokeWidth="8" />
        <circle cx="230" cy="125" r="60" fill={palette.billFront.circle} />
        <circle cx="85" cy="125" r="25" fill={palette.billFront.circle} />
        <circle cx="375" cy="125" r="25" fill={palette.billFront.circle} />
        <rect x="130" y="-5" width="90" height="260" fill={palette.billFront.highlight} opacity="0.85" />
      </g>

      <g transform="translate(160, 100)">{renderDollarCoin(palette.coinA, 0.6, 60, 70)}</g>
      <g transform="translate(380, 60)">{renderDollarCoin(palette.coinB, 0.7, 60, 70)}</g>
      <g transform="translate(600, 160)">{renderDollarCoin(palette.coinC, 0.65, 60, 70)}</g>

      {includeBottomCoins ? (
        <>
          <g transform="translate(260, 420)">
            <g transform="translate(0, 40)">
              <rect x="0" y="0" width="160" height="40" rx="20" fill={palette.stack.layerADark} />
              <rect x="0" y="-10" width="160" height="40" rx="20" fill={palette.stack.layerAMid} />
              <ellipse cx="80" cy="5" rx="60" ry="12" fill={palette.stack.layerAEllipse} />
            </g>
            <g transform="translate(0, 15)">
              <rect x="0" y="0" width="160" height="40" rx="20" fill={palette.stack.layerBDark} />
              <rect x="0" y="-10" width="160" height="40" rx="20" fill={palette.stack.layerBMid} />
              <ellipse cx="80" cy="5" rx="60" ry="12" fill={palette.stack.layerBEllipse} />
            </g>
            <g transform="translate(15, -10) scale(1.1)">
              <circle cx="70" cy="70" r="70" fill={palette.stack.topCoin.shadow} />
              <circle cx="70" cy="65" r="70" fill={palette.stack.topCoin.mid} />
              <circle cx="70" cy="65" r="62" fill={palette.stack.topCoin.face} />
              <text
                x="70"
                y="80"
                fontFamily="Arial, sans-serif"
                fontSize="70"
                textAnchor="middle"
                fill={palette.stack.topCoin.text}
                fontWeight="bold"
              >
                $
              </text>
            </g>
          </g>

          <g transform="translate(420, 270)">
            <rect x="0" y="20" width="150" height="180" rx="20" fill={palette.roll.body} />
            <rect x="0" y="20" width="150" height="25" rx="10" fill={palette.roll.stripeA} />
            <rect x="0" y="45" width="150" height="25" rx="10" fill={palette.roll.stripeB} />
            <rect x="0" y="70" width="150" height="25" rx="10" fill={palette.roll.stripeA} />
            <rect x="0" y="95" width="150" height="25" rx="10" fill={palette.roll.stripeB} />
            <rect x="0" y="120" width="150" height="25" rx="10" fill={palette.roll.stripeA} />
            <rect x="0" y="145" width="150" height="25" rx="10" fill={palette.roll.stripeB} />
            <rect x="0" y="170" width="150" height="25" rx="10" fill={palette.roll.stripeA} />
            <path
              d="M 12 25 L 12 185"
              stroke={palette.roll.shine}
              strokeWidth="6"
              strokeLinecap="round"
              opacity="0.4"
            />
            <g transform="translate(0, 10)">
              <circle cx="75" cy="0" r="75" fill={palette.roll.topCoinMid} />
              <circle cx="75" cy="0" r="56" fill={palette.roll.topCoinFace} />
              <text
                x="75"
                y="20"
                fontFamily="Arial, sans-serif"
                fontSize="75"
                textAnchor="middle"
                fill={palette.roll.topCoinText}
                fontWeight="bold"
              >
                $
              </text>
            </g>
          </g>
        </>
      ) : null}
    </g>
  );

  const lightPalette: MoneyScenePalette = {
    billBack: {
      outer: '#1B7343',
      mid: '#31A366',
      inner: '#52C486',
      corner: '#258550',
      circle: '#258550',
    },
    billFront: {
      outer: '#258550',
      mid: '#3EBD7D',
      inner: '#62E29F',
      corner: '#3EBD7D',
      circle: '#3EBD7D',
      highlight: '#FFF7C2',
      fold: '#258550',
    },
    coinA: { shadow: '#D99100', mid: '#FFCA28', face: '#FFE082', text: '#D99100' },
    coinB: { shadow: '#E69D00', mid: '#FFD54F', face: '#FFF9C4', text: '#E69D00' },
    coinC: { shadow: '#D99100', mid: '#FFCA28', face: '#FFE082', text: '#D99100' },
    stack: {
      layerADark: '#C68400',
      layerAMid: '#FFA000',
      layerAEllipse: '#FFCA28',
      layerBDark: '#D99100',
      layerBMid: '#FFB300',
      layerBEllipse: '#FFD54F',
      topCoin: { shadow: '#E69D00', mid: '#FFCA28', face: '#FFF59D', text: '#E69D00' },
    },
    roll: {
      body: '#D99100',
      stripeA: '#FFB300',
      stripeB: '#FFA000',
      shine: '#FFE082',
      topCoinMid: '#FFCA28',
      topCoinFace: '#FFF59D',
      topCoinText: '#D99100',
    },
  };

  const darkPalette: MoneyScenePalette = {
    billBack: {
      outer: '#065F46',
      mid: '#047857',
      inner: '#10B981',
      corner: '#064E3B',
      circle: '#064E3B',
    },
    billFront: {
      outer: '#047857',
      mid: '#059669',
      inner: '#34D399',
      corner: '#059669',
      circle: '#059669',
      highlight: '#FEF9C3',
      fold: '#047857',
    },
    coinA: { shadow: '#B45309', mid: '#FBBF24', face: '#FDE68A', text: '#B45309' },
    coinB: { shadow: '#C2410C', mid: '#FCD34D', face: '#FEF3C7', text: '#C2410C' },
    coinC: { shadow: '#B45309', mid: '#FBBF24', face: '#FDE68A', text: '#B45309' },
    stack: {
      layerADark: '#B45309',
      layerAMid: '#D97706',
      layerAEllipse: '#FBBF24',
      layerBDark: '#C68400',
      layerBMid: '#E69D00',
      layerBEllipse: '#FCD34D',
      topCoin: { shadow: '#C2410C', mid: '#FBBF24', face: '#FEF9C3', text: '#C2410C' },
    },
    roll: {
      body: '#B45309',
      stripeA: '#D97706',
      stripeB: '#F59E0B',
      shine: '#FDE68A',
      topCoinMid: '#FBBF24',
      topCoinFace: '#FEF9C3',
      topCoinText: '#B45309',
    },
  };

  const subtleLightPalette: MoneyScenePalette = {
    billBack: {
      outer: '#4ADE80',
      mid: '#86EFAC',
      inner: '#A7F3D0',
      corner: '#34D399',
      circle: '#34D399',
    },
    billFront: {
      outer: '#34D399',
      mid: '#6EE7B7',
      inner: '#D1FAE5',
      corner: '#4ADE80',
      circle: '#4ADE80',
      highlight: '#FEF9C3',
      fold: '#34D399',
    },
    coinA: { shadow: '#FCD34D', mid: '#FDE68A', face: '#FEF3C7', text: '#F59E0B' },
    coinB: { shadow: '#FBBF24', mid: '#FCD34D', face: '#FEF9C3', text: '#F59E0B' },
    coinC: { shadow: '#FCD34D', mid: '#FDE68A', face: '#FEF3C7', text: '#F59E0B' },
    stack: {
      layerADark: '#FCD34D',
      layerAMid: '#FDE68A',
      layerAEllipse: '#FEF08A',
      layerBDark: '#FBBF24',
      layerBMid: '#FCD34D',
      layerBEllipse: '#FDE68A',
      topCoin: { shadow: '#FBBF24', mid: '#FCD34D', face: '#FEF9C3', text: '#F59E0B' },
    },
    roll: {
      body: '#FBBF24',
      stripeA: '#FCD34D',
      stripeB: '#FDE68A',
      shine: '#FEF3C7',
      topCoinMid: '#FCD34D',
      topCoinFace: '#FEF9C3',
      topCoinText: '#F59E0B',
    },
  };

  const subtleDarkPalette: MoneyScenePalette = {
    billBack: {
      outer: '#134E4A',
      mid: '#115E59',
      inner: '#5EEAD4',
      corner: '#0F766E',
      circle: '#0F766E',
    },
    billFront: {
      outer: '#0F766E',
      mid: '#14B8A6',
      inner: '#99F6E4',
      corner: '#14B8A6',
      circle: '#14B8A6',
      highlight: '#FEF9C3',
      fold: '#0F766E',
    },
    coinA: { shadow: '#CA8A04', mid: '#FDE68A', face: '#FEF9C3', text: '#CA8A04' },
    coinB: { shadow: '#D97706', mid: '#FEF08A', face: '#FEFCE8', text: '#D97706' },
    coinC: { shadow: '#CA8A04', mid: '#FDE68A', face: '#FEF9C3', text: '#CA8A04' },
    stack: {
      layerADark: '#CA8A04',
      layerAMid: '#EAB308',
      layerAEllipse: '#FDE68A',
      layerBDark: '#D97706',
      layerBMid: '#FACC15',
      layerBEllipse: '#FEF08A',
      topCoin: { shadow: '#D97706', mid: '#FDE68A', face: '#FEFCE8', text: '#D97706' },
    },
    roll: {
      body: '#CA8A04',
      stripeA: '#EAB308',
      stripeB: '#FACC15',
      shine: '#FEF9C3',
      topCoinMid: '#FDE68A',
      topCoinFace: '#FEFCE8',
      topCoinText: '#CA8A04',
    },
  };

  const activeLightPalette = tone === 'subtle' ? subtleLightPalette : lightPalette;
  const activeDarkPalette = tone === 'subtle' ? subtleDarkPalette : darkPalette;
  const lightSceneOpacity = opaque ? '1' : tone === 'subtle' ? '0.22' : '0.24';
  const darkSceneOpacity = opaque ? '1' : tone === 'subtle' ? '0.22' : '0.3';

  return (
    <svg
      className={className}
      viewBox={inline ? TITLE_INLINE_VIEWBOX : '0 0 800 600'}
      preserveAspectRatio={inline ? 'xMidYMax meet' : undefined}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <defs>
        <filter id={shadowId} x="-10%" y="-10%" width="130%" height="130%">
          <feDropShadow dx="2" dy="4" stdDeviation="4" floodOpacity="0.15" />
        </filter>
        {renderScene(lightSceneId, activeLightPalette, !inline)}
        {renderScene(darkSceneId, activeDarkPalette, !inline)}
      </defs>

      <g className="dark:hidden" filter={`url(#${shadowId})`} opacity={lightSceneOpacity}>
        <use href={`#${lightSceneId}`} />
      </g>
      <g className="hidden dark:block" filter={`url(#${shadowId})`} opacity={darkSceneOpacity}>
        <use href={`#${darkSceneId}`} />
      </g>
    </svg>
  );
};
